import {
  ActivityDefinitionPdfTemplate,
  DependencyError,
  sanitizeFilename
} from '@cumu/shared';
import { target, targets } from '@github/catalyst';
import { busySubmitButtons } from '../../forms/utilities';
import { mergePdfs } from '../../pdf';
import { zipFiles } from '../../zip';
import { controller } from '../controller';
import { PdfViewerElement } from './pdf-viewer';

declare const PDFLib: typeof import('pdf-lib');

@controller('prepare-pdf-documents')
export class PreparePdfDocumentsElement extends HTMLElement {
  @targets declare viewerElements: PdfViewerElement[];
  @targets declare instructionElements: HTMLElement[];
  @target declare nextButton: HTMLButtonElement;
  @target declare previousButton: HTMLButtonElement;
  @target declare downloadForm: HTMLFormElement;
  @target declare templatesScript: HTMLScriptElement;
  current = 0;
  hasChanges = false;
  isSubmitting = false;

  selectTemplate(event: Event) {
    const index = +(event.target as HTMLSelectElement).value;
    if (index === this.current) {
      return;
    }
    this.viewerElements[this.current].classList.add('v-hidden');
    this.instructionElements[this.current].hidden = true;
    this.current = index;
    this.viewerElements[this.current].classList.remove('v-hidden');
    this.instructionElements[this.current].hidden = false;
  }

  // showPrevious() {
  //   this.viewerElements[this.current].classList.add('v-hidden');
  //   this.instructionElements[this.current].hidden = true;
  //   this.current--;
  //   this.viewerElements[this.current].classList.remove('v-hidden');
  //   this.instructionElements[this.current].hidden = false;
  //   this.previousButton.disabled = this.current === 0;
  //   this.nextButton.disabled = this.current === this.viewerElements.length - 1;
  // }

  // showNext() {
  //   this.viewerElements[this.current].classList.add('v-hidden');
  //   this.instructionElements[this.current].hidden = true;
  //   this.current++;
  //   this.viewerElements[this.current].classList.remove('v-hidden');
  //   this.instructionElements[this.current].hidden = false;
  //   this.previousButton.disabled = this.current === 0;
  //   this.nextButton.disabled = this.current === this.viewerElements.length - 1;
  // }

  async download(event: CustomEvent<FormData>) {
    event.preventDefault();
    if (this.isSubmitting) {
      return;
    }
    try {
      this.isSubmitting = true;
      await Promise.resolve();
      busySubmitButtons(this.downloadForm, true);
      const pdfTemplates = JSON.parse(
        this.templatesScript.textContent!
      ) as Pick<
        ActivityDefinitionPdfTemplate,
        'title' | 'start_page' | 'end_page'
      >[];
      let { filename, type: submitType } = Object.fromEntries(event.detail) as {
        filename: string;
        type?: string;
      };
      const pdfs = await Promise.all(this.viewerElements.map(el => el.save()));
      if (submitType === 'zip') {
        if (!/\.zip$/i.test(filename)) {
          filename += '.zip';
        }
        const data = await zipFiles(
          pdfs.map((data, i) => ({
            filename: sanitizeFilename(pdfTemplates[i].title + '.pdf'),
            data
          })),
          'Created by ' +
            this.getAttribute('user_name')! +
            ' at ' +
            this.getAttribute('organization_name')!
        );
        const url = URL.createObjectURL(
          new Blob([data], { type: 'application/zip' })
        );
        download(url, sanitizeFilename(filename));
        return;
      }

      if (!/\.pdf$/i.test(filename)) {
        filename += '.pdf';
      }
      const data = await mergePdfs({
        title: filename,
        author:
          this.getAttribute('user_name')! +
          ' at ' +
          this.getAttribute('organization_name')!,
        pdfs: pdfs.map((data, i) => ({
          title: pdfTemplates[i].title,
          data,
          start_page: pdfTemplates[i].start_page,
          end_page: pdfTemplates[i].end_page,
          right_signature_placeholders:
            submitType === 'send'
              ? [
                  {
                    name: '[s:a:r                 ]',
                    page_index: pdfTemplates[i].start_page - 1,
                    x: 70,
                    y: 140 + 10,
                    size: 14
                  },
                  {
                    name: '[d:a:r              ]',
                    page_index: pdfTemplates[i].start_page - 1,
                    x: 460,
                    y: 140,
                    size: 14
                  }

                  // {
                  //   name: '[s:b:r                 ]',
                  //   page_index: pdfTemplates[i].start_page - 1,
                  //   x: 70,
                  //   y: 100 + 10,
                  //   size: 14
                  // },
                  // {
                  //   name: '[d:b:r              ]',
                  //   page_index: pdfTemplates[i].start_page - 1,
                  //   x: 460,
                  //   y: 100,
                  //   size: 14
                  // }

                  // {
                  //   name: '[s:c:r                 ]',
                  //   page_index: pdfTemplates[i].start_page - 1,
                  //   x: 70,
                  //   y: 20 + 10,
                  //   size: 14
                  // },
                  // {
                  //   name: '[d:c:r              ]',
                  //   page_index: pdfTemplates[i].start_page - 1,
                  //   x: 460,
                  //   y: 20,
                  //   size: 14
                  // }
                ]
              : undefined
        }))
      });
      if (submitType === 'pdf') {
        const url = URL.createObjectURL(
          new Blob([data], { type: 'application/pdf' })
        );
        download(url, sanitizeFilename(filename));
        return;
      } else if (submitType === 'send') {
        const response = await fetch(
          `${location.origin}/api/sending-requests`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/pdf'
            },
            body: data
          }
        );
        if (!response.ok) {
          throw new DependencyError(response, await response.text());
        }
      } else {
        throw new Error(`Unexpected submit type: ${submitType}`);
      }
    } finally {
      busySubmitButtons(this.downloadForm, false);
      this.isSubmitting = false;
    }
  }

  pdfFieldChanged() {
    if (this.hasChanges) {
      return;
    }
    this.hasChanges = true;
    window.addEventListener('beforeunload', event => {
      event.preventDefault();
      event.returnValue = 'You have unsaved work.'; // browser overrides this text
    });
  }
}

function download(blobUrl: string, filename: string) {
  const a = document.createElement('a');
  if (!a.click) {
    throw new Error('DownloadManager: "a.click()" is not supported.');
  }
  a.href = blobUrl;
  a.target = '_parent';
  // Use a.download if available. This increases the likelihood that
  // the file is downloaded instead of opened by another PDF plugin.
  if ('download' in a) {
    a.download = filename;
  }
  // <a> must be in the document for recent Firefox versions,
  // otherwise .click() is ignored.
  (document.body || document.documentElement).append(a);
  a.click();
  a.remove();
}
